/** @jsxImportSource theme-ui */
import { Box, Text, Flex, Heading, Spinner } from 'theme-ui';
import React from 'react';
import { Formik } from 'formik';
import {
    useGetDiscountAgreementQuery,
    useEditDiscountAgreementsMutation,
} from '@/store/apiService';
import { toast } from 'react-toastify';
import { DiscountAgreementForm } from './DiscountAgreementForm';
import ConnectDiscountAgreement from './ConnectDiscountAgreement';
import { getErrorMessage } from '@/utils/helpers';
import { convertStatusNameToValue, resolveValidToForUpdate } from './discountAgreementStatus';

type Props = {
    discountAgreementId: string;
    onSubmitSuccess?: () => void;
};

const EditDiscountAgreement: React.FC<Props> = ({
    discountAgreementId,
    onSubmitSuccess = () => { },
}: Props) => {
    const discountAgreements = useGetDiscountAgreementQuery({ id: discountAgreementId });
    const [editDiscountAgreement, editDiscountAgreementState] = useEditDiscountAgreementsMutation();
    // TODO: populate form based on the new response

    if (discountAgreements.isLoading) {
        return <Spinner size={16} />;
    }
    if (discountAgreements.data) {
        const discountAgreement = discountAgreements.data;
        const chargePlanDiscount = discountAgreement?.ChargePlanDiscounts?.[0];
        const productGroupDiscount = chargePlanDiscount?.ProductGroupDiscounts?.[0];
        const productPricingDiscount = productGroupDiscount?.ProductPricingDiscounts?.[0];
        const discountPeriod = discountAgreement.DiscountPeriod;
        const length = discountPeriod?.Length;

        const initialValues = {
            _articleType: '4',
            DiscountAgreementStatus: convertStatusNameToValue(discountAgreement.Status),
            Name: discountAgreement.Name,
            Description: discountAgreement.Description,
            ValidFrom: discountAgreement.ValidFrom,
            ValidTo: resolveValidToForUpdate(discountAgreement),
            DiscountPeriod: {
                Length: length ? { Unit: length.Unit, Value: length.Value } : undefined,
                PeriodIterationCount: discountPeriod?.PeriodIterationCount,
                PeriodKind: discountPeriod?.PeriodKind,
            },
            ChargePlanDiscounts: {
                ChargePlanId: chargePlanDiscount?.ChargePlanId,
                ProductGroupDiscounts: {
                    ProductGroupId: productGroupDiscount?.ProductGroupId,
                    ProductPricingDiscounts: {
                        ProductId: productPricingDiscount?.ProductId,
                        DiscountType: productPricingDiscount?.DiscountType,
                        UnitPriceDiscount: productPricingDiscount?.UnitPriceDiscount,
                    },
                },
            },
            DiscountAgreementId: discountAgreementId,
        };

        return (
            <>
                <Flex
                    backgroundColor="Background"
                    sx={{ flexDirection: 'column', borderRadius: 'sm' }}
                >
                    <Flex
                        p="2"
                        sx={{
                            gap: 2,
                            '@media only screen and (max-width:60em)': {
                                flexDirection: 'column',
                            },
                            borderRadius: 'sm',
                        }}
                    >
                        <Box
                            sx={{
                                flexBasis: '50%',
                                borderRadius: 'sm',
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                borderColor: 'gray.2',
                                alignSelf: 'flex-start',
                            }}
                            p="3"
                        >
                            <Formik
                                initialValues={initialValues}
                                enableReinitialize
                                onSubmit={(values, { resetForm, setErrors, setSubmitting }) => {
                                    return editDiscountAgreement(values)
                                        .unwrap()
                                        .then((res) => {
                                            onSubmitSuccess();
                                        })
                                        .catch((err) => {
                                            setSubmitting(false);
                                            toast.error(getErrorMessage(err), { autoClose: 5000 });
                                        });
                                }}
                            >
                                {(formProps) => {
                                    return (
                                        <>
                                            <DiscountAgreementForm
                                                additionalErrors={editDiscountAgreementState.error}
                                                {...formProps}
                                            />
                                        </>
                                    );
                                }}
                            </Formik>
                        </Box>
                    </Flex>
                </Flex>
            </>
        );
    }
};

export default EditDiscountAgreement;
